// ----------------------------------------------------------------------------
// Library: Just-Validate
// ----------------------------------------------------------------------------
//
// Form validation library with pre-configured and custom settings.
// Refer to https://just-validate.dev/examples for additonal customization.
//
// Usage: Add the class 'form--validate' to a form.
// ----------------------------------------------------------------------------


// -------------------------------------
// Dependencies
// -------------------------------------

import JustValidate from 'just-validate';
import JustValidatePluginDate from 'just-validate-plugin-date';


// -------------------------------------
// Localize Validation Error Messages
// -------------------------------------

// Generate Just Validate "dictLocal" Object
// We'll consider everything "English", but Shopify will provide localized translations
const dictLocale = window?.translations?.formValidationErrors ? Object.keys(window?.translations?.formValidationErrors) : []
    .map((key, value) => ({
        key,
        dict: {
            en: value
        }
    }));


// -------------------------------------
// Functionality
// -------------------------------------

const initFormValidation = () => {
    const forms = document.querySelectorAll('.form--validate');

    forms.forEach((form) => {

        // Initialize the Validator
        const validator = new JustValidate(form, {
            validateBeforeSubmitting: true,
        }, dictLocale);


        // Helper to loop over the form fields to define rules
        let formRules = {};
        const forEachField = (selector, callback) => {
            form.querySelectorAll(selector).forEach((field) => {
                const fieldName = field.getAttribute('name');
                if (!fieldName) return; // Abort if the field doesn't have a name
                if (!formRules[fieldName]) formRules[fieldName] = [];
                callback(field, fieldName);
            });
        };


        forEachField('input[required]', (field, fieldName) => {
            formRules[fieldName].push({ 'rule' : 'required' });
        });

        forEachField('input[type="email"]', (field, fieldName) => {
            formRules[fieldName].push({ 'rule' : 'email' });
        });

        forEachField('input[type="number"]', (field, fieldName) => {
            formRules[fieldName].push({ 'rule' : 'number' });
        });

        forEachField('input[maxlength]', (field, fieldName) => {
            formRules[fieldName].push({
                'rule' : 'maxLength',
                'value' : parseInt(field.getAttribute('maxlength'), 10)
            });
        });

        forEachField('input[minlength]', (field, fieldName) => {
            formRules[fieldName].push({
                'rule' : 'minLength',
                'value' : parseInt(field.getAttribute('minlength'), 10)
            });
        });

        forEachField('input[min]', (field, fieldName) => {
            formRules[fieldName].push({
                'rule' : 'minNumber',
                'value' : parseInt(field.getAttribute('min'), 10)
            });
        });

        forEachField('input[max]', (field, fieldName) => {
            formRules[fieldName].push({
                'rule' : 'maxNumber',
                'value' : parseInt(field.getAttribute('max'), 10)
            });
        });

        forEachField('input[type="file"][required]:not([data-min-files])', (field, fieldName) => {
            formRules[fieldName].push({
                'rule'         : 'minFilesCount',
                'value'        : 1,
                'errorMessage' : window?.translations?.formValidationErrors?.required
            });
        });

        forEachField('input[type="file"][multiple][data-min-files]', (field, fieldName) => {
            formRules[fieldName].push({
                'rule' : 'minFilesCount',
                'value' : parseInt(field.getAttribute('data-min-files'), 10)
            });
        });

        forEachField('input[type="file"][multiple][data-max-files]', (field, fieldName) => {
            formRules[fieldName].push({
                'rule' : 'maxFilesCount',
                'value' : parseInt(field.getAttribute('data-max-files'), 10)
            });
        });

        forEachField('input[pattern]', (field, fieldName) => {
            formRules[fieldName].push({
                'rule'         : 'customRegexp',
                'value'        : new RegExp(field.getAttribute('pattern')),
                'errorMessage' : field.getAttribute('data-pattern-error') || field.getAttribute('title') || window?.translations?.formValidationErrors?.customRegexp,
            });
        });

        forEachField('input[data-regex]:not([pattern])', (field, fieldName) => {
            formRules[fieldName].push({
                'rule'         : 'customRegexp',
                'value'        : new RegExp(field.getAttribute('data-regex')),
                'errorMessage' : field.getAttribute('data-regex-error') || field.getAttribute('title') || window?.translations?.formValidationErrors?.customRegexp,
            });
        });

        forEachField('input[name="date"], input[type="date"], [data-rule="date"]', (field, fieldName) => {
            formRules[fieldName].push({
                plugin: JustValidatePluginDate(() => ({
                    format: 'yyyy-mm-dd',
                })),
                errorMessage: window?.translations?.formValidationErrors?.date,
            });
        });

        forEachField('input[type="tel"]:not([pattern]), [data-rule="phone"]:not([pattern])', (field, fieldName) => {
            formRules[fieldName].push({
                rule: 'customRegexp',
                value: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                errorMessage: window?.translations?.formValidationErrors?.phone_international,
            });

            // formRules[fieldName].push({
            //     rule: 'customRegexp',
            //     value: /^\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
            //     errorMessage: window?.translations?.formValidationErrors?.phone_10digit,
            // });
        });


        // Register the Rules
        Object.entries(formRules).forEach(([fieldName, rules]) => {
            if (rules.length > 0) {
                validator.addField('[name=' + fieldName + ']', rules);
            }
        });

    });
};

document.addEventListener('DOMContentLoaded', () => {
    initFormValidation();
});
